import type { AfterViewInit } from '@angular/core';
import { Component, inject, input, signal } from '@angular/core';
import { OnyxDropdownDirective } from '../../../directives/onyx-dropdown.directive';
import { OnyxOverlayPosition } from '../../../enums';
import {
  ActionHelper,
  AddressHelper,
  CoordinatesHelper,
} from '../../../helpers';
import type { OnyxOptional } from '../../../interfaces';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxAddressService } from '../../../services/onyx-address.service';
import { OnyxToastService } from '../../../services/onyx-toast.service';
import { OnyxAddressType } from '../../address-input/enums';
import type { OnyxCoordinates } from '../../address-input/interfaces';
import type { OnyxMapContextMenuOptions } from '../interfaces';

@Component({
  selector: 'onyx-map-context-menu',
  imports: [OnyxDropdownDirective],
  templateUrl: './onyx-map-context-menu.component.html',
  styleUrl: './onyx-map-context-menu.component.scss',
})
export class OnyxMapContextMenuComponent implements AfterViewInit {
  private readonly toastService = inject(OnyxToastService);
  private readonly addressService = inject(OnyxAddressService);

  public readonly coordinates = input.required<OnyxCoordinates>();
  public readonly options = input(null, {
    transform: (
      options: OnyxOptional<OnyxMapContextMenuOptions[]>,
    ): OnyxMapContextMenuOptions[] => [
      this.DEFAULT_OPTIONS,
      ...(options ?? []),
    ],
  });
  public readonly close = input.required<() => void>();

  protected readonly I18N = `${I18N_NAMESPACE}.map`;
  protected readonly DEFAULT_OPTIONS: OnyxMapContextMenuOptions = {
    id: '0.default',
    options: [
      {
        name: `${this.I18N}.copyLocation`,
        value: (coordinates) => {
          ActionHelper.copy(CoordinatesHelper.toString(coordinates), false);
          this.toastService.showSuccess({
            message: `${this.I18N}.copyLocationSuccess`,
          });
        },
        leftIcon: {
          name: 'location-outlined',
          size: 20,
        },
      },
      {
        name: `${this.I18N}.checkAddress`,
        value: (coordinates) =>
          this.addressService
            .geocode(
              [OnyxAddressType.CUSTOM],
              CoordinatesHelper.toString(coordinates),
              1,
            )
            .subscribe({
              next: (result) => {
                const address = result.items.at(0);
                if (address) {
                  const label = AddressHelper.composeLabel(address);
                  this.toastService.showSuccess({
                    message: label,
                    buttons: [
                      {
                        text: `${I18N_NAMESPACE}.common.copy`,
                        callback: () =>
                          ActionHelper.copy(label, this.toastService),
                        color: 'black',
                        variant: 'ghost',
                        size: 24,
                      },
                    ],
                  });
                } else {
                  this.toastService.showError({
                    message: `${this.I18N}.checkAddressFailed`,
                  });
                }
              },
            }),
        leftIcon: {
          name: 'search-outlined',
          size: 20,
        },
      },
    ],
  };

  protected readonly OnyxOverlayPosition = OnyxOverlayPosition;

  protected attached = signal(false);

  public ngAfterViewInit(): void {
    requestAnimationFrame(() => this.attached.set(true));
  }
}
